
import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { LeafletModule } from '@bluehalo/ngx-leaflet';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import * as esri from 'esri-leaflet';
import * as esriLeafletVector from 'esri-leaflet-vector';
import {
  Feature,
  FeatureCollection,
  LineString,
  MultiPolygon,
  Polygon,
  Position,
} from 'geojson';
import * as L from 'leaflet';
import {
  featureGroup,
  FeatureGroup,
  latLng,
  LatLngBoundsExpression,
  tileLayer,
} from 'leaflet';
import 'leaflet.fullscreen';
import 'leaflet.pattern';
import {
  combineLatest,
  first,
  mergeMap,
  Observable,
  ReplaySubject,
  Subject,
  switchMap,
  tap,
} from 'rxjs';
import * as sharedModels from 'src/app/models/shared-models';
import { IReGroupByVmStatusIdAndFeatures } from 'src/app/models/shared-models';
import {
  IRECategoryGroupingAndGeom as IRegionalEcosystemGrouping,
  ISpatialMapLayerData,
} from 'src/app/veg-wizard/models/veg-wizard-models';
import { Logger } from 'src/app/veg-wizard/services/logger-service';
import { VegWizardDataService } from 'src/app/veg-wizard/services/veg-wizard-data.service';
import { environment } from 'src/environments/environment';
import { MapService } from '../services/map.service';
import {
  IMapComponentModel,
  IMapEventEmitterModel,
} from './map-component-model';

import * as turf from '@turf/turf';

/**
 * Shared Map Component for the Veg Self Assess Wizard
 * 
 * 
Leaflet licence - https://leafletjs.com/
BSD 2-Clause License

Copyright (c) 2010-2025, Volodymyr Agafonkin
Copyright (c) 2010-2011, CloudMade
All rights reserved.

Redistribution and use in source and binary forms, with or without
modification, are permitted provided that the following conditions are met:

1. Redistributions of source code must retain the above copyright notice, this
   list of conditions and the following disclaimer.

2. Redistributions in binary form must reproduce the above copyright notice,
   this list of conditions and the following disclaimer in the documentation
   and/or other materials provided with the distribution.

THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS "AS IS"
AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE
IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE ARE
DISCLAIMED. IN NO EVENT SHALL THE COPYRIGHT HOLDER OR CONTRIBUTORS BE LIABLE
FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL
DAMAGES (INCLUDING, BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR
SERVICES; LOSS OF USE, DATA, OR PROFITS; OR BUSINESS INTERRUPTION) HOWEVER
CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT LIABILITY,
OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY OUT OF THE USE
OF THIS SOFTWARE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.
 */

@UntilDestroy()
@Component({
  selector: 'app-map',
  templateUrl: './map.component.html',
  styleUrls: ['./map.component.css'],
  imports: [ReactiveFormsModule, LeafletModule],
})
export class MapComponent implements OnInit, OnDestroy {
  @Input() inputMapComponentModel: IMapComponentModel | undefined = undefined;
  @Output() mapDataEvent = new EventEmitter<IMapEventEmitterModel>();

  lot: string = this.inputMapComponentModel?.lot || '';
  plan: string = this.inputMapComponentModel?.plan || '';

  // the primary map$. Allows us to subscribe to map configuration changes.
  private map$: ReplaySubject<L.Map> = new ReplaySubject(1);

  // Map max and min zoom contstants for all layers.
  private ZOOM_MAX = 16;
  private ZOOM_MIN = 10;
  private ZOOM_DEFAULT = 10;
  private ZOOM_MAX_VECTOR_LAYER = 13; // zoom limitation on the ESRI map vector format

  // A collection of base layers, with the QLD Imagery as the default.
  private baseLayers = {
    'QLD Imagery': esri.tiledMapLayer({
      url: 'https://spatial-img.information.qld.gov.au/arcgis/rest/services/Basemaps/LatestStateProgram_AllUsers/ImageServer',
      maxZoom: this.ZOOM_MAX,
      minZoom: this.ZOOM_MIN,
    }),
    'Street map': tileLayer(
      'http://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
      { maxZoom: this.ZOOM_MAX, minZoom: this.ZOOM_MIN, attribution: '...' }
    ),
    'QLD Basemap': esriLeafletVector.vectorTileLayer(
      'https://spatial.information.qld.gov.au/arcgis/rest/services/Hosted/Basemaps_QldBase_Pastel/VectorTileServer',
      {
        zoom: this.ZOOM_DEFAULT,
        maxZoom: this.ZOOM_MAX,
        minZoom: this.ZOOM_MIN,
      }
    ),
  };

  private featureLayerUrl = environment.vegSpatialFeatureLayerUrl;

  public markers = [];
  public lotplanfeature: FeatureGroup = new L.FeatureGroup();
  public cadastreLayer: FeatureGroup = new L.FeatureGroup();
  public cadastreLabels: L.LayerGroup = new L.LayerGroup();
  public vegMapRegionalEcosystemLayers: ISpatialMapLayerData[] = [];
  public vegBiotaLayers: ISpatialMapLayerData[] = [];

  // The starting point for the map, current coords are brisbane,
  // but ideally, this will change to the lot plan provided.
  private LATLONG_MAP_ORIGIN = latLng(-27.470125, 153.021072);

  layersControl = {
    baseLayers: {},
    overlays: {},
  } as any;

  options = {
    zoom: this.ZOOM_DEFAULT,
    center: this.LATLONG_MAP_ORIGIN,
    layers: [
      this.baseLayers['QLD Imagery'], // default base layer to show
    ],
  };

  drawnItems: FeatureGroup = featureGroup();

  // Shape options are set on new layers created by the user. They
  // define how the polygon will look, how it is filled etc.
  private shapeOptions = {
    clickable: true,
    fill: true,
    opacity: 0.7,
    stroke: true,
    weight: 4,
    editing: {
      enable: true,
    },
    className: 'shape-option',
  } as L.PolylineOptions;

  // layer options for drawn items. these are required
  // by leaflet draw and are typed from 'L.Control.DrawConstructorOptions'
  drawOptions: L.Control.DrawConstructorOptions = {};

  constructor(
    private mapService: MapService,
    private vwds: VegWizardDataService,
    private logger: Logger
  ) {}

  ngOnDestroy(): void {
    //TODO
  }

  ngOnInit(): void {
    if (!this.inputMapComponentModel) {
      this.logger.warning(
        'inputMapComponentModel is empty',
        this.inputMapComponentModel
      );
      return;
    }
    const lotplan = this.vwds.getLotPlanModel();
    if (
      this.inputMapComponentModel?.lot == undefined ||
      this.inputMapComponentModel?.plan == undefined
    ) {
      this.inputMapComponentModel.lot = lotplan?.lot ?? '';
      this.inputMapComponentModel.plan = lotplan?.plan ?? '';
    }

    this.lot = this.inputMapComponentModel?.lot.toUpperCase();
    this.plan = this.inputMapComponentModel?.plan.toUpperCase();

    // re-draw existing saved geojson layer to the map
    if (this.inputMapComponentModel?.lodgementShapeGeoJson) {
      const existingDrawnGeoJson: FeatureCollection = JSON.parse(
        this.inputMapComponentModel?.lodgementShapeGeoJson
      );

      for (let feature of existingDrawnGeoJson.features) {
        if (feature.geometry.type === 'LineString') {
          const line = feature.geometry as LineString;
          const cords = line.coordinates.map((p: Position) => {
            return L.latLng(p[1], p[0]);
          });
          this.drawnItems.addLayer(L.polyline(cords));
        }

        if (feature.geometry.type === 'Polygon') {
          const poly = feature.geometry as Polygon;
          const cords = poly.coordinates[0].map((p: Position) => {
            return L.latLng(p[1], p[0]);
          });

          this.drawnItems.addLayer(L.polygon(cords));
        }
      }
    }

    // Need to do this in the ngInit so lineDrawMode can be calulated.
    this.drawOptions = {
      position: 'topleft',
      fullscreenControl: true,
      fullscreenControlOptions: {
        position: 'topleft',
      },
      draw: {
        polyline: !this.inputMapComponentModel.lineSelectMode
          ? false
          : {
              allowIntersection: false,
              showLength: true,
              shapeOptions: this.shapeOptions,
              drawError: {
                color: '#e1e100', // Color the shape will turn when intersects
                message:
                  '<strong>Warning: <strong> Intersecting points are not permitted',
              },
              editing: {
                enable: true,
              },
              precision: { m: 3 },
              draggable: true,
            },
        circle: false,
        circlemarker: false,
        marker: false,
        polygon: this.inputMapComponentModel.lineSelectMode
          ? false
          : {
              allowIntersection: false, // Restricts shapes to simple polygons
              draggable: true,
              showArea: true,
              showLength: true,
              precision: { m: 3 },
              drawError: {
                color: '#e1e100', // Color the shape will turn when intersects
                message:
                  '<strong>Warning: <strong> Intersecting points are not permitted',
              },
              shapeOptions: this.shapeOptions,
              editing: {
                enable: true,
              },
            },
        rectangle: this.inputMapComponentModel.lineSelectMode
          ? false
          : {
              shapeOptions: this.shapeOptions,
              showArea: true,
              showLength: true,
              editing: {
                enable: true,
              },
            },
      },
      edit: {
        featureGroup: this.drawnItems,
        selectedPathOptions: {
          maintainColor: true,
          moveMarkers: true,
        },
        poly: {
          allowIntersection: false,
        },
      },
    } as L.Control.DrawConstructorOptions;
  }

  /**
   * Handles the map ready event. This is our trigger to being adding additional features
   * to the map such as legends, scales and drawing controls.
   * @param map the leaflet map
   */
  onMapReady(map: L.Map) {
    const start = new Date().getTime();
    map.attributionControl.setPrefix(''); // Remove "Leaflet" link
    this.initLotPlanMainLayer$(map, this.lot, this.plan)
      .pipe(
        first(),
        untilDestroyed(this),

        switchMap(() =>
          combineLatest([
            this.initCadastrelayer$(map),
            this.initAllVegLayersOverlay$(this.lot, this.plan),
          ])
        ),
        mergeMap(() =>
          combineLatest([
            this.initRegionalEcosystemLayer$(this.lot, this.plan),
            this.initBiotaVegLayer$(
              map,
              this.lot,
              this.plan,
              sharedModels.BiotaLayerNameEnum.essentialHabitat,
              environment.vegBiotaEssentialHabitatLayerId,
              sharedModels.BiotaColourDisplayEnum.essentialHabitat
            ),
            this.initBiotaVegLayer$(
              map,
              this.lot,
              this.plan,
              sharedModels.BiotaLayerNameEnum.waterCourse,
              environment.vegBiotaWaterCourseLayerId,
              sharedModels.BiotaColourDisplayEnum.waterCourse
            ),
            this.initBiotaVegLayer$(
              map,
              this.lot,
              this.plan,
              sharedModels.BiotaLayerNameEnum.wetLand,
              environment.vegBiotaWetLandLayerId,
              sharedModels.BiotaColourDisplayEnum.wetLand
            ),
          ])
        )
      )
      .subscribe(() => {
        this.initLegend(map);
        const end = new Date().getTime();
        const time = end - start;
        console.log('Execution time: ' + time + 'ms');
      });

    this.initDrawing(map);
    this.initScale(map);
    this.limitBaseMapZoomLevel(map);

    // broadcast that we've changed the map.
    this.setupMapDrawEvents(map);
    this.map$.next(map);
  }

  limitBaseMapZoomLevel(map: L.Map) {
    map.on('baselayerchange', (e) => {
      const selectedLayerName = e.name;
      if (selectedLayerName === 'QLD Basemap') {
        map.setMaxZoom(this.ZOOM_MAX); // Business wants to unrestrict zoom level. alternatively use this.ZOOM_MAX_VECTOR_LAYER variable.
      } else {
        map.setMaxZoom(this.ZOOM_MAX);
      }
      map.setMinZoom(this.ZOOM_MIN);
    });
  }

  setupMapDrawEvents(map: L.Map) {
    map.on(L.Draw.Event.CREATED, (e: any) => {
      this.emitMapDataToParentComponent('Map Add');
    });
    map.on(L.Draw.Event.EDITED, (e: any) => {
      this.emitMapDataToParentComponent('Map Edit');
    });
    map.on(L.Draw.Event.DELETED, (e: any) => {
      this.emitMapDataToParentComponent('Map Delete');
    });
  }

  private emitMapDataToParentComponent(eventType: string) {
    const model: IMapEventEmitterModel = {
      landClearingGeoJson: this.drawnItems.toGeoJSON(),
      lotPlanGeoJson: this.lotplanfeature.toGeoJSON(),
      landClearingLayer: this.drawnItems.getLayers(),
    } as IMapEventEmitterModel;

    // emit so parent component can detect data changes in this component
    this.mapDataEvent.emit(model);
  }

  /**
   * Initialise the map scale
   * @param map the leaflet map
   */
  initScale(map: L.Map) {
    L.control
      .scale({ metric: true, imperial: false, position: 'bottomright' })
      .addTo(map);
  }

  initLotPlanMainLayer$(
    map: L.Map,
    lot: string,
    plan: string
  ): Observable<boolean> {
    const query = esri.query({
      url: this.featureLayerUrl,
    });

    const qr$ = new Subject<boolean>();

    query.where("LOTPLAN = '" + lot + plan + "'");

    query.run((error: unknown, featureCollection: FeatureCollection) => {
      const lotplanStyle = {
        weight: 2,
        opacity: 1,
        color: 'red', //Outline color
        fillOpacity: 0,
      };
      this.lotplanfeature = L.geoJSON(featureCollection, {
        style: lotplanStyle,
      }).addTo(map);

      map.fitBounds(this.lotplanfeature.getBounds());
      map.setMaxBounds(this.lotplanfeature.getBounds());
      map.setMinZoom(
        map.getBoundsZoom(map.options.maxBounds as LatLngBoundsExpression)
      );

      qr$.next(true);
      qr$.complete();
    });

    return qr$;
  }

  /**
   * Initialise the map layers and establish a default layer for Veg map
   * @param _map the leaflet map
   */
  initAllVegLayersOverlay$(lot: string, plan: string) {
    this.layersControl.baseLayers = this.baseLayers;
    const vegSpatialCategoryLayerId = environment.vegSpatialCategoryLayerId;

    return this.mapService
      .getLotPlanVegetationFeatureGeoJsonLayerData$(
        lot,
        plan,
        vegSpatialCategoryLayerId
      )
      .pipe(
        tap((geojson: string) => {
          const vegCategoryOverlays = this.getVegCategoryOverlays(geojson);

          const allLayersOverlay = {
            'Cadastre Parcels': this.cadastreLayer,
            ...vegCategoryOverlays,
          };
          this.layersControl.overlays = allLayersOverlay;
        })
      );
  }

  /*
   * Gets data of the LotPlan cadastre and displays it as an outline
   */
  initCadastrelayer$(map: L.Map): Observable<boolean> {
    const query = esri.query({
      url: this.featureLayerUrl,
    });

    const qr$ = new Subject<boolean>();

    const y = map.getSize().y,
      x = map.getSize().x;
    // calculate the distance the one side of the map to the other using the haversine formula
    const maxMeters = map
      .containerPointToLatLng([0, y])
      .distanceTo(map.containerPointToLatLng([x, y]));
    // calculate how many meters each pixel represents

    query.nearby(map.getCenter(), maxMeters / 2);

    query.run((error: unknown, featureCollection: FeatureCollection) => {
      const lotPlanStyle = {
        weight: 0.5,
        opacity: 1,
        color: 'green', //Outline color
        fillOpacity: 0,
      };

      this.cadastreLayer = L.geoJSON(featureCollection, {
        style: lotPlanStyle,
      });

      L.geoJSON(featureCollection, {
        onEachFeature: (feature: any, layer: any) => {
          if (feature.properties && feature.properties.LOTPLAN) {
            const label = L.marker(layer.getBounds().getCenter(), {
              icon: L.divIcon({
                className: 'lotplanlabel',
                html: feature.properties['LOTPLAN'],
                iconSize: [100, 40],
              }),
            });
            this.cadastreLabels.addLayer(label);
          }
        },
      });

      qr$.next(true);
      qr$.complete();
    });

    return qr$;
  }

  /**
   * Initialise the map legend and assign the correct colour values (using constant values) based on the given layer types and scenarios
   * @param map the leaflet map
   */
  private initLegend(map: L.Map) {
    const legend = new L.Control({ position: 'bottomleft' });

    // default colours and labels
    const colourGradesArray = ['red', 'green'];
    const labelsArray = ['Current Lot Plan', 'Cadastre Parcels'];

    if (this.layersControl.overlays[sharedModels.CategoryLabelEnum.catA]) {
      colourGradesArray.push(sharedModels.CategoryColourDisplayEnum.catA);
      labelsArray.push(`${sharedModels.CategoryLabelEnum.catA}`);
    }
    if (this.layersControl.overlays[sharedModels.CategoryLabelEnum.catB]) {
      colourGradesArray.push(sharedModels.CategoryColourDisplayEnum.catB);
      labelsArray.push(`${sharedModels.CategoryLabelEnum.catB}`);
    }
    if (this.layersControl.overlays[sharedModels.CategoryLabelEnum.catC]) {
      colourGradesArray.push(sharedModels.CategoryColourDisplayEnum.catC);
      labelsArray.push(`${sharedModels.CategoryLabelEnum.catC}`);
    }
    if (this.layersControl.overlays[sharedModels.CategoryLabelEnum.catR]) {
      colourGradesArray.push(sharedModels.CategoryColourDisplayEnum.catR);
      labelsArray.push(`${sharedModels.CategoryLabelEnum.catR}`);
    }
    if (this.layersControl.overlays[sharedModels.CategoryLabelEnum.catX]) {
      colourGradesArray.push(sharedModels.CategoryColourDisplayEnum.catX);
      labelsArray.push(`${sharedModels.CategoryLabelEnum.catX}`);
    }

    // Regional ecosystems group layers
    if (
      this.layersControl.overlays[
        `${sharedModels.RegionalEcosystemDescEnum.reCatAorBAreaEndangered} RE`
      ]
    ) {
      colourGradesArray.push(
        sharedModels.RegionalEcosystemColourEnum.reCatAorBAreaEndangered
      );
      labelsArray.push(
        `${sharedModels.RegionalEcosystemDescEnum.reCatAorBAreaEndangered} RE`
      );
    }
    if (
      this.layersControl.overlays[
        `${sharedModels.RegionalEcosystemDescEnum.reCatAorBAreaContainConcern} RE`
      ]
    ) {
      colourGradesArray.push(
        sharedModels.RegionalEcosystemColourEnum.reCatAorBAreaContainConcern
      );
      labelsArray.push(
        `${sharedModels.RegionalEcosystemDescEnum.reCatAorBAreaContainConcern} RE`
      );
    }
    if (
      this.layersControl.overlays[
        `${sharedModels.RegionalEcosystemDescEnum.reCatAorBAreaLeastConcern} RE`
      ]
    ) {
      colourGradesArray.push(
        sharedModels.RegionalEcosystemColourEnum.reCatAorBAreaLeastConcern
      );
      labelsArray.push(
        `${sharedModels.RegionalEcosystemDescEnum.reCatAorBAreaLeastConcern} RE`
      );
    }
    if (
      this.layersControl.overlays[
        `${sharedModels.RegionalEcosystemDescEnum.reCatCorRAreaEndangered} RE`
      ]
    ) {
      colourGradesArray.push(
        sharedModels.RegionalEcosystemColourEnum.reCatCorRAreaEndangered
      );
      labelsArray.push(
        `${sharedModels.RegionalEcosystemDescEnum.reCatCorRAreaEndangered} RE`
      );
    }
    if (
      this.layersControl.overlays[
        `${sharedModels.RegionalEcosystemDescEnum.reCatCorRAreaContainConcern} RE`
      ]
    ) {
      colourGradesArray.push(
        sharedModels.RegionalEcosystemColourEnum.reCatCorRAreaContainConcern
      );
      labelsArray.push(
        `${sharedModels.RegionalEcosystemDescEnum.reCatCorRAreaContainConcern} RE`
      );
    }
    if (
      this.layersControl.overlays[
        `${sharedModels.RegionalEcosystemDescEnum.reCatCorRAreaLeastConcern} RE`
      ]
    ) {
      colourGradesArray.push(
        sharedModels.RegionalEcosystemColourEnum.reCatCorRAreaLeastConcern
      );
      labelsArray.push(
        `${sharedModels.RegionalEcosystemDescEnum.reCatCorRAreaLeastConcern} RE`
      );
    }
    if (
      this.layersControl.overlays[
        `${sharedModels.RegionalEcosystemDescEnum.reNonRemnant} RE`
      ]
    ) {
      colourGradesArray.push(
        sharedModels.RegionalEcosystemColourEnum.reNonRemnant
      );
      labelsArray.push(
        `${sharedModels.RegionalEcosystemDescEnum.reNonRemnant}`
      );
    }
    if (
      this.layersControl.overlays[
        `${sharedModels.RegionalEcosystemDescEnum.reRemnant} RE`
      ]
    ) {
      colourGradesArray.push(
        sharedModels.RegionalEcosystemColourEnum.reRemnant
      );
      labelsArray.push(
        `${sharedModels.RegionalEcosystemDescEnum.reRemnant} RE`
      );
    }
    if (
      this.layersControl.overlays[
        `${sharedModels.RegionalEcosystemDescEnum.reWater} RE`
      ]
    ) {
      colourGradesArray.push(sharedModels.RegionalEcosystemColourEnum.reWater);
      labelsArray.push(`${sharedModels.RegionalEcosystemDescEnum.reWater}`);
    }
    if (
      this.layersControl.overlays[
        `${sharedModels.RegionalEcosystemDescEnum.reRegrowthVegetation} RE`
      ]
    ) {
      colourGradesArray.push(
        sharedModels.RegionalEcosystemColourEnum.reRegrowthVegetation
      );
      labelsArray.push(
        `${sharedModels.RegionalEcosystemDescEnum.reRegrowthVegetation} RE`
      );
    }

    // Biota layers
    if (
      this.layersControl.overlays[
        sharedModels.BiotaLayerNameEnum.essentialHabitat
      ]
    ) {
      colourGradesArray.push(
        sharedModels.BiotaColourDisplayEnum.essentialHabitat
      );
      labelsArray.push(`${sharedModels.BiotaLayerNameEnum.essentialHabitat}`);
    }
    if (
      this.layersControl.overlays[sharedModels.BiotaLayerNameEnum.waterCourse]
    ) {
      colourGradesArray.push(sharedModels.BiotaColourDisplayEnum.waterCourse);
      labelsArray.push(`${sharedModels.BiotaLayerNameEnum.waterCourse}`);
    }
    if (this.layersControl.overlays[sharedModels.BiotaLayerNameEnum.wetLand]) {
      colourGradesArray.push(sharedModels.BiotaColourDisplayEnum.wetLand);
      labelsArray.push(`${sharedModels.BiotaLayerNameEnum.wetLand}`);
    }

    legend.onAdd = (map: L.Map) => {
      const div = L.DomUtil.create('div', 'maplegend'),
        colourGrades = colourGradesArray,
        labels = labelsArray;
      div.innerHTML = '<strong>Map Legend</strong> <br/>';

      // loop through our density intervals and generate a label with a colored square for each interval
      for (let i = 0; i < colourGrades.length; i++) {
        div.innerHTML +=
          '<i class="fa fa-circle" style="color:' +
          colourGrades[i] +
          '"></i> ' +
          ' ' +
          labels[i] +
          '<br/>';
      }

      return div;
    };

    legend.addTo(map);
  }

  /*
   * handles Regional Ecosystem geojson layers, based on the given lotplan
   * the end result is group based on the Regional Ecosystem types such as Endangered, Of Concern and Least Concern, non-rem etc.
   * it also attempts to union the layer based on matching RE types
   */
  initRegionalEcosystemLayer$(lot: string, plan: string) {
    const vegRegionalEcosystemLayerId =
      environment.vegSpatialRegionalEcosystemLayerId;

    return this.mapService
      .getLotPlanVegetationFeatureGeoJsonLayerData$(
        lot,
        plan,
        vegRegionalEcosystemLayerId
      )
      .pipe(
        tap((geoJsonResult: string) => {
          if (!geoJsonResult) {
            return;
          }

          let featureCollection: FeatureCollection<Polygon>;
          if (typeof geoJsonResult === 'string') {
            featureCollection = this.jsonRequiresDoubleParsing(geoJsonResult)
              ? JSON.parse(JSON.parse(geoJsonResult))
              : JSON.parse(geoJsonResult);
          } else {
            featureCollection = geoJsonResult;
          }

          const defaultRegionalEcosstemMapCategoryLayerStyle = {
            weight: 0.5,
            opacity: 1,
            fillOpacity: 0.5,
          };

          const reGroupList: IRegionalEcosystemGrouping[] = [];

          if (
            featureCollection.features != null &&
            featureCollection.features.length > 0
          ) {
            const reFeaturesGroupedById: IReGroupByVmStatusIdAndFeatures[] =
              this.mapService.getRegionalEcosystemFeaturesGroupedByVmStatId(
                featureCollection
              );

            reFeaturesGroupedById.forEach((grouped) => {
              const matchingRERule =
                sharedModels.regionalEcosystemInfoAndColours.find((x) =>
                  x.reVmStatLookupIds.some(
                    (s) => s.toString() == grouped.reVmStatId
                  )
                );

              // only process RE's that have matching colours / based on the rules
              if (matchingRERule) {
                const regionalEcosystemGrouping = {
                  reRuleStringId: matchingRERule?.reVmStatusString,
                  reRuleStringForDisplay: matchingRERule?.reRuleIdDescription,
                  reVmRuleId: grouped.reVmStatId,
                  regionalEcosystems: [
                    ...new Set(
                      grouped.features.map((x: any) =>
                        this.mapService.getPropCaseInsensitive(
                          x.properties,
                          'RE'
                        )
                      )
                    ),
                  ],
                  spatialMapLayerData: {} as ISpatialMapLayerData,
                } as IRegionalEcosystemGrouping;

                let unionFeatures: Feature<Polygon | MultiPolygon> | null =
                  null;
                if (grouped.features != null && grouped.features.length > 0) {
                  grouped.features.forEach((feature: any) => {
                    if (!unionFeatures) {
                      unionFeatures = turf.feature(
                        feature.geometry,
                        feature.properties
                      );
                    } else {
                      const poly1 = turf.feature(unionFeatures.geometry);
                      const poly2 = turf.feature(feature.geometry);
                      unionFeatures = turf.union({
                        type: 'FeatureCollection',
                        features: [poly1, poly2],
                      });
                    }
                  });

                  const regionalEcosystemInfoAndColour =
                    sharedModels.regionalEcosystemInfoAndColours.find(
                      (x) =>
                        x.reVmStatusString ===
                        regionalEcosystemGrouping.reRuleStringId
                    );
                  const selectedColour = regionalEcosystemInfoAndColour
                    ? regionalEcosystemInfoAndColour.colourDisplay
                    : 'gray';

                  const regionalEcosystemLayer = L.geoJSON(unionFeatures!, {
                    style: {
                      ...defaultRegionalEcosstemMapCategoryLayerStyle,
                      color: selectedColour,
                    },
                  });

                  // finally prepare and present this RE grouped data to the leaflet screen
                  const spatialREData = {
                    id: regionalEcosystemGrouping.reRuleStringForDisplay,
                    layer: regionalEcosystemLayer,
                  } as ISpatialMapLayerData;
                  regionalEcosystemGrouping.spatialMapLayerData = spatialREData;
                }

                if (
                  regionalEcosystemGrouping.spatialMapLayerData.layer.getLayers()
                    .length > 0
                ) {
                  reGroupList.push(regionalEcosystemGrouping);
                }
              }
            });
          }

          this.vegMapRegionalEcosystemLayers = reGroupList.map(
            (x) => x.spatialMapLayerData
          );

          if (this.vegMapRegionalEcosystemLayers) {
            this.vegMapRegionalEcosystemLayers =
              this.vegMapRegionalEcosystemLayers.sort((a, b) =>
                ('' + a.id).localeCompare(b.id)
              ); // sort alphabetically

            this.vegMapRegionalEcosystemLayers.forEach(
              (regionalEcosystemData: ISpatialMapLayerData) => {
                const reId = regionalEcosystemData.id;
                let reDisplay = reId.toLowerCase().startsWith('water') // dont display 'RE' suffix for water
                  ? reId
                  : `${reId} RE`;

                // dynamically add the RE Id's and bind to the RE feature layer
                this.layersControl.overlays = {
                  ...this.layersControl.overlays,
                  [reDisplay]: regionalEcosystemData.layer,
                };
              }
            );
          }
        })
      );
  }

  /*
   * Handles the Biota layers such as Essential Habitats, Water course etc
   * this queries the Gis / ESRI spatial map layer and displays the geojson feature
   */
  initBiotaVegLayer$(
    map: L.Map,
    lot: string,
    plan: string,
    layerName: string,
    layerId: number,
    colour: string
  ) {
    return this.mapService
      .getLotPlanBiotaVegetationFeatureGeoJsonLayerData$(lot, plan, layerId)
      .pipe(
        tap((geoJsonResult: string) => {
          if (!geoJsonResult) {
            return;
          }

          let featureCollection: FeatureCollection<Polygon>;
          if (typeof geoJsonResult === 'string') {
            featureCollection = this.jsonRequiresDoubleParsing(geoJsonResult)
              ? JSON.parse(JSON.parse(geoJsonResult))
              : JSON.parse(geoJsonResult);
          } else {
            featureCollection = geoJsonResult;
          }

          const combinedFeatureCollection =
            this.mapService.combineFeatureCollection(featureCollection);

          const layerStyle = {
            weight: 3,
            opacity: 1,
            fillOpacity: 0.5,
          };

          if (
            combinedFeatureCollection.features != null &&
            combinedFeatureCollection.features.length > 0
          ) {
            const isEssentialHabitatLayer =
              layerName === sharedModels.BiotaLayerNameEnum.essentialHabitat;
            const isWetLandLayer =
              layerName === sharedModels.BiotaLayerNameEnum.wetLand;

            const stripes = new L.StripePattern({
              patternContentUnits: 'objectBoundingBox',
              patternUnits: 'objectBoundingBox',
              weight: 0.01,
              spaceWeight: 0.01,
              height: 0.02,
              opacity: 0.75,
              color: sharedModels.BiotaColourDisplayEnum.essentialHabitat,
              angle: 45,
            });

            const stripesWetland = new L.StripePattern({
              patternContentUnits: 'objectBoundingBox',
              patternUnits: 'objectBoundingBox',
              weight: 0.01,
              spaceWeight: 0.01,
              height: 0.02,
              opacity: 0.75,
              color: sharedModels.BiotaColourDisplayEnum.wetLand,
              angle: 135,
            });

            stripes.addTo(map);
            stripesWetland.addTo(map);

            combinedFeatureCollection.features.forEach((feature: any) => {
              const currentLayer = L.geoJSON(feature, {
                style: {
                  ...layerStyle,
                  color: colour,
                  fillPattern: isEssentialHabitatLayer
                    ? stripes
                    : isWetLandLayer
                    ? stripesWetland
                    : undefined,
                },
              });

              const spatialMapData = {
                id: layerName,
                layer: currentLayer,
              } as ISpatialMapLayerData;
              this.vegBiotaLayers.push(spatialMapData);
            });
          }

          if (this.vegBiotaLayers) {
            this.vegBiotaLayers.forEach((data: ISpatialMapLayerData) => {
              const layerIdName = `${data.id}`;
              this.layersControl.overlays = {
                ...this.layersControl.overlays,
                [layerIdName]: data.layer,
              };
            });
          }
        })
      );
  }

  /**
   * Initialise the drawing controls and events.
   * @param map the leaflet map
   */
  private initDrawing(map: L.Map) {
    // hack to correct bug in leaflet draw, as described here:
    // https://github.com/Leaflet/Leaflet.draw/issues/1026
    (window as any).type = true;

    const drawControl = new L.Control.Draw(this.drawOptions);
    map.addControl(drawControl);

    map.on('draw:created', (e) => this.onDrawCreated(e));

    // create a fullscreen button and add it to the map
    L.control
      .fullscreen({
        position: 'topleft', // change the position of the button can be topleft, topright, bottomright or bottomleft, default topleft
        title: 'Go to Fullscreen mode!', // change the title of the button, default Full Screen
        titleCancel: 'Exit fullscreen mode', // change the title of the button when fullscreen is on, default Exit Full Screen
        content: '⛶', // change the content of the button, can be HTML, default null
        forceSeparateButton: true, // force separate button to detach from zoom buttons, default false
        forcePseudoFullscreen: true, // force use of pseudo full screen even if full screen API is available, default false
        fullscreenElement: false, // Dom element to render in full screen, false by default, fallback to map._container
      })
      .addTo(map);

    // events are fired when entering or exiting fullscreen.
    map.on('enterFullscreen', function () {
      console.log('entered fullscreen');
    });

    map.on('exitFullscreen', function () {
      console.log('exited fullscreen');
    });
  }

  /* Displays mapping layer data such as Category A, B, C, R, X, and assign a particular colour coding
   */
  private getVegCategoryOverlays(geoJsonResult: string): any | undefined {
    if (!geoJsonResult) {
      return;
    }

    let vegCategoryLayerGeoJson: any;
    if (typeof geoJsonResult === 'string') {
      vegCategoryLayerGeoJson = this.jsonRequiresDoubleParsing(geoJsonResult)
        ? JSON.parse(JSON.parse(geoJsonResult))
        : JSON.parse(geoJsonResult);
    } else {
      vegCategoryLayerGeoJson = geoJsonResult;
    }

    const defaultVegMapCategoryLayerStyle = {
      weight: 0.5,
      opacity: 1,
      fillOpacity: 0.5,
    };

    if (!vegCategoryLayerGeoJson.features) {
      return undefined;
    }

    const catAFeatures: Feature<Polygon | MultiPolygon>[] =
      vegCategoryLayerGeoJson.features.filter(
        (x: Feature<Polygon, any>) =>
          this.mapService.getPropCaseInsensitive(x.properties, 'RVM_CAT') ===
          sharedModels.CategoryEnum.catA
      ) ?? [];

    const catBFeatures: Feature<Polygon | MultiPolygon>[] =
      vegCategoryLayerGeoJson.features.filter(
        (x: Feature<Polygon, any>) =>
          this.mapService.getPropCaseInsensitive(x.properties, 'RVM_CAT') ===
          sharedModels.CategoryEnum.catB
      );

    const catCFeatures: Feature<Polygon | MultiPolygon>[] =
      vegCategoryLayerGeoJson.features.filter(
        (x: Feature<Polygon, any>) =>
          this.mapService.getPropCaseInsensitive(x.properties, 'RVM_CAT') ===
          sharedModels.CategoryEnum.catC
      );

    const catRFeatures: Feature<Polygon | MultiPolygon>[] =
      vegCategoryLayerGeoJson.features.filter(
        (x: Feature<Polygon, any>) =>
          this.mapService.getPropCaseInsensitive(x.properties, 'RVM_CAT') ===
          sharedModels.CategoryEnum.catR
      );

    const catXFeatures: Feature<Polygon | MultiPolygon>[] =
      vegCategoryLayerGeoJson.features.filter(
        (x: Feature<Polygon, any>) =>
          this.mapService.getPropCaseInsensitive(x.properties, 'RVM_CAT') ===
          sharedModels.CategoryEnum.catX
      );

    const overlays = {} as any;

    if (catAFeatures) {
      const catAUnionFeature = this.mapService.unionFeatures(catAFeatures);

      if (catAUnionFeature) {
        overlays[sharedModels.CategoryLabelEnum.catA] = L.geoJSON(
          catAUnionFeature,
          {
            style: {
              ...defaultVegMapCategoryLayerStyle,
              color: sharedModels.CategoryColourDisplayEnum.catA,
            },
          }
        );
      }
    }

    if (catBFeatures) {
      const catBUnionFeatures = this.mapService.unionFeatures(catBFeatures);

      if (catBUnionFeatures) {
        overlays[sharedModels.CategoryLabelEnum.catB] = L.geoJSON(
          catBUnionFeatures,
          {
            style: {
              ...defaultVegMapCategoryLayerStyle,
              color: sharedModels.CategoryColourDisplayEnum.catB,
            },
          }
        );
      }
    }

    if (catCFeatures) {
      const catCUnionFeatures = this.mapService.unionFeatures(catCFeatures);

      if (catCUnionFeatures) {
        overlays[sharedModels.CategoryLabelEnum.catC] = L.geoJSON(
          catCUnionFeatures,
          {
            style: {
              ...defaultVegMapCategoryLayerStyle,
              color: sharedModels.CategoryColourDisplayEnum.catC,
            },
          }
        );
      }
    }

    if (catRFeatures) {
      const catRUnionFeatures = this.mapService.unionFeatures(catRFeatures);

      if (catRUnionFeatures) {
        overlays[sharedModels.CategoryLabelEnum.catR] = L.geoJSON(
          catRUnionFeatures,
          {
            style: {
              ...defaultVegMapCategoryLayerStyle,
              color: sharedModels.CategoryColourDisplayEnum.catR,
            },
          }
        );
      }
    }

    if (catXFeatures) {
      const catXUnionFeatures = this.mapService.unionFeatures(catXFeatures);

      if (catXUnionFeatures) {
        overlays[sharedModels.CategoryLabelEnum.catX] = L.geoJSON(
          catXUnionFeatures,
          {
            style: {
              ...defaultVegMapCategoryLayerStyle,
              color: sharedModels.CategoryColourDisplayEnum.catX,
            },
          }
        );
      }
    }

    return overlays;
  }

  /**
   * Handles shape / drawing creation events that fire after a user has
   * finished drawing a shape.
   * @param e A L.DrawEvents.Created event.
   */
  private onDrawCreated(e: any) {
    const createdEvent = e as L.DrawEvents.Created;
    this.drawnItems.addLayer(createdEvent.layer);
  }

  private jsonRequiresDoubleParsing(json: string): boolean {
    if (json.startsWith('{"type')) {
      return false;
    }

    return true;
  }
}
